import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, Wrench, Users, Bike, Boxes, ShoppingBag, Settings, LogOut, Plus, Receipt, CalendarClock, CalendarDays, KeyRound, Warehouse, CalendarRange, AlertTriangle, Menu, X, FileText, CreditCard, Crown } from "lucide-react";
import { APP_NAME } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, id: "dashboard", end: true },
  { to: "/schede", label: "Schede Officina", icon: Wrench, id: "schede" },
  { to: "/preventivi", label: "Preventivo di riparazione", icon: FileText, id: "preventivi" },
  { to: "/cassa", label: "Vendita al banco", icon: Receipt, id: "cassa" },
  { to: "/clienti", label: "Clienti", icon: Users, id: "clienti" },
  { to: "/bici", label: "Bici Clienti", icon: Bike, id: "bici" },
  { to: "/agenda", label: "Agenda", icon: CalendarDays, id: "agenda" },
  { to: "/noleggi", label: "Noleggi", icon: KeyRound, id: "noleggi" },
  { to: "/flotta", label: "Flotta noleggio", icon: Warehouse, id: "flotta", end: true },
  { to: "/flotta/calendario", label: "Calendario flotta", icon: CalendarRange, id: "calendario-flotta" },
  { to: "/tagliandi", label: "Promemoria tagliando", icon: CalendarClock, id: "tagliandi" },
  { to: "/ricambi", label: "Magazzino Ricambi", icon: Boxes, id: "ricambi" },
  { to: "/prodotti", label: "Prodotti", icon: ShoppingBag, id: "prodotti" },
  { to: "/impostazioni", label: "Impostazioni", icon: Settings, id: "impostazioni" },
];

const Sidebar = ({ open, onClose }) => {
  const { user } = useAuth();
  const voci = [...NAV, ...(user?.role === "admin" ? [{ to: "/abbonamento", label: "Abbonamento", icon: CreditCard, id: "abbonamento" }] : []),
    ...(user?.superadmin ? [{ to: "/superadmin", label: "Super amministratore", icon: Crown, id: "superadmin" }] : [])];
  return (
  <aside className={`fixed inset-y-0 left-0 z-40 flex w-[260px] flex-col bg-slate-900 text-slate-300 transition-transform duration-200 lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}>
    <div className="flex h-16 items-center justify-between border-b border-slate-800 px-6">
      <div className="flex min-w-0 items-center gap-2.5">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-orange-600 text-white"><Bike className="h-5 w-5" /></div>
        <div className="min-w-0 font-heading text-[15px] font-extrabold leading-tight text-white"><span className="block truncate" data-testid="sidebar-nome-negozio">{user?.negozio?.nome || APP_NAME}</span><span className="block text-[10px] font-semibold uppercase tracking-[0.2em] text-orange-400" data-testid="sidebar-app-name">{APP_NAME}</span></div>
      </div>
      <button className="lg:hidden" onClick={onClose} data-testid="sidebar-close-button"><X className="h-5 w-5" /></button>
    </div>
    <nav className="flex-1 space-y-1 overflow-y-auto p-3">
      {voci.map(({ to, label, icon: Icon, id, end }) => (
        <NavLink key={to} to={to} end={end} onClick={onClose} data-testid={`nav-${id}-link`}
          className={({ isActive }) => `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-orange-600 text-white" : "hover:bg-slate-800 hover:text-white"}`}>
          <Icon className="h-4 w-4" /> {label}
        </NavLink>
      ))}
    </nav>
  </aside>
  );
};

const TrialBanner = () => {
  const { user } = useAuth();
  const n = user?.negozio;
  if (!n || n.stato !== "prova") return null;
  const testo = n.attivo ? <>Periodo di prova gratuita: <b>{n.giorni_prova} giorni rimasti</b></> : <b>Prova gratuita terminata</b>;
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 bg-orange-50 px-4 py-2 text-sm text-orange-800 sm:px-8" data-testid="trial-banner">
      <span>{testo}</span>
      {user.role === "admin" && <NavLink to="/abbonamento" className="font-semibold underline" data-testid="trial-banner-abbonati">Attiva l'abbonamento</NavLink>}
    </div>
  );
};

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [low, setLow] = useState(0);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get("/dashboard").then((r) => setLow(r.data.ricambi_bassi.length + r.data.prodotti_bassi.length)).catch(() => {});
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar open={open} onClose={() => setOpen(false)} />
      {open && <div className="fixed inset-0 z-30 bg-slate-900/40 lg:hidden" onClick={() => setOpen(false)} />}
      <div className="lg:pl-[260px]">
        <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-slate-200 bg-white/90 px-4 backdrop-blur-md sm:px-8">
          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="sidebar-open-button"><Menu className="h-5 w-5" /></button>
          <Button data-testid="header-new-scheda-button" onClick={() => navigate("/schede/nuova")} className="bg-orange-600 hover:bg-orange-700">
            <Plus className="mr-1 h-4 w-4" /> <span className="hidden sm:inline">Nuova scheda lavoro</span><span className="sm:hidden">Scheda</span>
          </Button>
          <div className="ml-auto flex items-center gap-3">
            <button data-testid="header-low-stock-indicator" onClick={() => navigate("/ricambi")}
              className={`flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-bold ${low ? "bg-rose-50 text-rose-700 ring-1 ring-rose-200" : "text-slate-400"}`} title="Scorte basse">
              <AlertTriangle className="h-4 w-4" /> {low}
            </button>
            <div className="hidden text-right sm:block">
              <p className="text-sm font-semibold text-slate-900" data-testid="header-user-name">{user?.name}</p>
              <p className="text-[11px] text-slate-500">{user?.email}</p>
            </div>
            <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-button" title="Esci"><LogOut className="h-4 w-4" /></Button>
          </div>
        </header>
        <TrialBanner /><main className="mx-auto max-w-[1400px] p-4 sm:p-8"><Outlet /></main>
      </div>
    </div>
  );
}
